import { assertLayoutMatches, updateQmFiledsOnPage, updateQmInfos, metrics, roundThreeDecimals } from './metrics/metrics.js';
import { normalizeConfig, toRenderOrdering } from './config.js';
import { allowsAttributes, allowsCategories, createState, lookupFirstDimension, reconcile, toRequest } from './constraint-state.js';
import { initConstraintMenu } from './constraint-menu.js';
import { METRICS } from './metrics/directions.js';
import { fetchDataset } from './data/dataset-loader.js';
import { createUserDataset, describeReport, initialConfig } from './data/user-csv.js';
import { LIMITS, canEstimate, formatDuration, remainingSeconds, runSize, runWarnings, sizeInfo } from './size-guard.js';
import { retrieveDatasetData } from './data/dataset-categories.js';
import { resetHistories } from './sparkline.js';
import { downloadPng, downloadSvg } from 'svg-crowbar';

window.updateQmFiledsOnPage = updateQmFiledsOnPage;
window.updateQmInfos = updateQmInfos;

const urlParams = new URLSearchParams(window.location.search);

if (process.env.NODE_ENV !== 'production' || urlParams.get('assertLayout') === 'true') {
    window.assertLayoutMatches = assertLayoutMatches;
}

const datasetCache = {};
function loadDataset(name) {
    if (!datasetCache[name]) {
        datasetCache[name] = fetchDataset(name);
    }
    return datasetCache[name];
}

let dataset;
let chart;
let vis;

function drawParallelSets() {
    window.psOrdering = dataset.categories;
    syncConstraints();
    d3.select("#parallel-sets").selectAll("*").remove();
    chart = d3.parsets()
        .dimensions(dataset.categories.map(e => e.name))
        .on("sortDimensions", configurationChanged)
        .on("sortCategories", configurationChanged);
    vis = d3.select("#parallel-sets").append("svg")
        .attr("id", "vis-svg")
        .attr("width", chart.width())
        .attr("height", chart.height());
    const target = vis;
    return loadDataset(dataset.name).then(data => {
        // A newer draw may have replaced the SVG while the data was loading.
        if (target !== vis) return;
        window.psDataset = data;
        target.datum(data.rows).call(chart);
    });
}

// Draws a given configuration ([{ name, categories: [names] }]) of a dataset.
function showConfiguration(datasetName, config) {
    dataset = { name: datasetName, categories: toRenderOrdering(config) };
    return drawParallelSets();
}

function curves() {
    var t = vis.transition().duration(500);
    t.call(chart.tension(this.checked ? .5 : 1));
}

// Datasets of the analyst (todo/20): name -> dataset of data/user-csv.js; bundled datasets are fetched by name.
const userDatasets = {};
const USER_PREFIX = 'upload:';

function isUserDataset(name = dataset.name) {
    return Object.prototype.hasOwnProperty.call(userDatasets, name);
}

// Parses a CSV file of the analyst and registers it; throws with a message for the analyst. The name cannot
// collide with a bundled dataset; a file with the same name replaces the earlier one.
function addUserDataset(fileName, text) {
    const label = String(fileName).replace(/^.*[\\/]/, '');
    const name = USER_PREFIX + label.replace(/\.[^.]*$/, '');
    const data = createUserDataset(name, text);
    data.label = label;
    data.size = sizeInfo(data, initialConfig(data));
    userDatasets[name] = data;
    datasetCache[name] = Promise.resolve(data);
    return data;
}

// Shows a dataset in its initial configuration: a bundled one by its name, one of the analyst by the name that
// addUserDataset returned.
function selectDataset(name) {
    const user = isUserDataset(name);
    // Size guard (todo/20, U5): a plot with very many ribbons can freeze the page; the analyst decides.
    if (user && userDatasets[name].size.ribbons > LIMITS.drawRibbons && !userDatasets[name].drawConfirmed) {
        const data = userDatasets[name];
        datasetStatus(`${data.label}: ${data.size.ribbons.toLocaleString('en-US')} ribbons in ${data.size.attributes} attributes; drawing them may take long or freeze the page.`,
            { error: true, action: { label: 'Draw anyway', run: () => { data.drawConfirmed = true; selectDataset(name); } } });
        return Promise.resolve();
    }
    const next = user ? { name, categories: toRenderOrdering(initialConfig(userDatasets[name])) } : retrieveDatasetData(name);
    optimizeAbort();
    document.getElementById("dataset-button").textContent = user ? userDatasets[name].label : next.label;
    datasetStatus(user ? describeReport(userDatasets[name].report, userDatasets[name].size.ribbons) : '');
    resetHistories();
    dataset = next;
    // A dataset chosen or loaded starts without constraints, also the one shown (todo/11 Decision 6).
    constraintDataset = null;
    updateLookupControls();
    return drawParallelSets();
}

function loadUserCsv(fileName, text) {
    const data = addUserDataset(fileName, text);
    updateUserMenu();
    return selectDataset(data.name);
}

// --- Upload of a CSV file (todo/20, U3): menu entry, drag and drop, status line; nothing leaves the browser -------

// action: { label, run } adds a button behind the text.
function datasetStatus(text, { error = false, action = null } = {}) {
    const status = document.getElementById('dataset-status');
    status.textContent = text;
    status.classList.toggle('error', error);
    if (!action) return;
    const button = Object.assign(document.createElement('button'), { type: 'button', className: 'btn btn-outline-dark btn-sm dataset-status-action', textContent: action.label });
    button.onclick = action.run;
    status.append(' ', button);
}

// Reads a File and shows it; a file that cannot be shown leaves the current dataset and reports the reason.
function loadUserFile(file, confirmed = false) {
    if (file.size > LIMITS.fileBytes && !confirmed) {
        datasetStatus(`${file.name}: ${(file.size / 2 ** 20).toFixed(0)} MB; reading a file of this size may take long or freeze the page.`,
            { error: true, action: { label: 'Read anyway', run: () => loadUserFile(file, true) } });
        return Promise.resolve();
    }
    return file.text()
        .then(text => loadUserCsv(file.name, text))
        .catch(error => { datasetStatus(`${file.name}: ${error.message}`, { error: true }); });
}

// One menu entry per loaded file, to switch back to it.
function updateUserMenu() {
    const entries = document.getElementById('dataset-user-entries');
    entries.replaceChildren(...Object.values(userDatasets).map(data => {
        const entry = document.createElement('a');
        entry.className = 'dropdown-item dataset-user-entry';
        entry.href = '#';
        entry.textContent = data.label;
        entry.onclick = () => { selectDataset(data.name); };
        return entry;
    }));
}

function initUpload() {
    const input = document.getElementById('dataset-file');
    document.getElementById('dataset-load').onclick = () => input.click();
    document.getElementById('dataset-upload').onclick = () => input.click();
    input.onchange = () => {
        const file = input.files[0];
        input.value = '';
        if (file) loadUserFile(file);
    };
    // Disabled lookup links (updateLookupControls) ignore clicks; their inline handlers do not run.
    document.getElementById('content-explorer').addEventListener('click', event => {
        if (!event.target.closest('a.lookup-disabled')) return;
        event.preventDefault();
        event.stopPropagation();
    }, true);
    // A file dropped anywhere on the Explorer page is loaded.
    const page = document.getElementById('content-explorer');
    const hasFile = event => event.dataTransfer && [...event.dataTransfer.types].includes('Files');
    page.addEventListener('dragover', event => {
        if (!hasFile(event)) return;
        event.preventDefault();
        page.classList.add('drop-target');
    });
    page.addEventListener('dragleave', event => { if (!page.contains(event.relatedTarget)) page.classList.remove('drop-target'); });
    page.addEventListener('drop', event => {
        if (!hasFile(event)) return;
        event.preventDefault();
        page.classList.remove('drop-target');
        if (event.dataTransfer.files[0]) loadUserFile(event.dataTransfer.files[0]);
    });
}

// --- Lookups (minimum, maximum, percentile) need the ground truth: benchmark datasets only (todo/20, U4) -----------

const LOOKUP_HINT = 'available for the benchmark datasets only';

function updateLookupControls() {
    const user = isUserDataset();
    document.querySelectorAll('#content-explorer button.btn-dark').forEach(control => {
        control.disabled = user;
        control.title = user ? LOOKUP_HINT : '';
    });
    document.querySelectorAll('#content-explorer a[onclick$="Percentile()"]').forEach(link => {
        link.classList.toggle('lookup-disabled', user);
        link.title = user ? LOOKUP_HINT : '';
    });
    if (user) document.getElementById('percentile-tooltip').style.display = 'none';
}

function loadConfigForParallelSets(datasetName, feature, option) {
    if (isUserDataset(datasetName)) return Promise.resolve();
    const data = {
        dataset: datasetName,
        feature,
        option,
        // Filter of the lookups: a first axis with fixed position and fixed order (todo/11 Decision 3).
        firstDimension: JSON.stringify(lookupFirstDimension(constraintState, shownConfig())),
    }
    const promise = fetch(window.location.origin + '/api/data', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    })
        .then(e => {
            if (!e.ok) {
                throw new Error('Lookup failed (HTTP ' + e.status + ').');
            }
            return e.json();
        })
        .then(e => showConfiguration(datasetName, e));
    return promise;
}

// Min/max functions

function overlapMin() {
    loadConfigForParallelSets(dataset.name, 'overlap', 'min');
}

function overlapMax() {
    loadConfigForParallelSets(dataset.name, 'overlap', 'max');
}

function slopeMin() {
    loadConfigForParallelSets(dataset.name, 'slope', 'min');
}

function slopeMax() {
    loadConfigForParallelSets(dataset.name, 'slope', 'max');
}

function orthogonalityMin() {
    loadConfigForParallelSets(dataset.name, 'orthogonality', 'min');
}

function orthogonalityMax() {
    loadConfigForParallelSets(dataset.name, 'orthogonality', 'max');
}

function numCrossingsMin() {
    loadConfigForParallelSets(dataset.name, 'crossings', 'min');
}

function numCrossingsMax() {
    loadConfigForParallelSets(dataset.name, 'crossings', 'max');
}

function crossingAngleMin() {
    loadConfigForParallelSets(dataset.name, 'relCrossingAngle', 'min');
}

function crossingAngleMax() {
    loadConfigForParallelSets(dataset.name, 'relCrossingAngle', 'max');
}

function numRibbonsMin() {
    loadConfigForParallelSets(dataset.name, 'relNumRibbons', 'min');
}

function numRibbonsMax() {
    loadConfigForParallelSets(dataset.name, 'relNumRibbons', 'max');
}

function widthVarianceMin() {
    loadConfigForParallelSets(dataset.name, 'widthSd', 'min');
}

function widthVarianceMax() {
    loadConfigForParallelSets(dataset.name, 'widthSd', 'max');
}

function mutualInfoMin() {
    loadConfigForParallelSets(dataset.name, 'mutualInfo', 'min');
}

function mutualInfoMax() {
    loadConfigForParallelSets(dataset.name, 'mutualInfo', 'max');
}

// Percentile functions

let percentileFeature;
let percentileValue;

function overlapPercentile() {
    percentileFeature = 'overlap';
    const t = document.getElementById('percentile-tooltip');
    t.style.display = 'inline';
}

function slopePercentile() {
    percentileFeature = 'slope';
    const t = document.getElementById('percentile-tooltip');
    t.style.display = 'inline';
}

function orthogonalityPercentile() {
    percentileFeature = 'orthogonality';
    const t = document.getElementById('percentile-tooltip');
    t.style.display = 'inline';
}

function numCrossingsPercentile() {
    percentileFeature = 'crossings';
    const t = document.getElementById('percentile-tooltip');
    t.style.display = 'inline';
}

function crossingAnglePercentile() {
    percentileFeature = 'relCrossingAngle';
    const t = document.getElementById('percentile-tooltip');
    t.style.display = 'inline';
}

function numRibbonsPercentile() {
    percentileFeature = 'relNumRibbons';
    const t = document.getElementById('percentile-tooltip');
    t.style.display = 'inline';
}

function widthVariancePercentile() {
    percentileFeature = 'widthSd';
    const t = document.getElementById('percentile-tooltip');
    t.style.display = 'inline';
}


function mutualInfoPercentile() {
    percentileFeature = 'mutualInfo';
    const t = document.getElementById('percentile-tooltip');
    t.style.display = 'inline';
}

function percentileValueChange(e) {
    percentileValue = e.value;
}

function featurePercentile() {
    loadConfigForParallelSets(dataset.name, percentileFeature, 'percentile/' + percentileValue);
    document.getElementById('percentile-tooltip').style.display = 'none';
}

async function crawlPngImages() {

    await new Promise(r => setTimeout(r, 10000));

    const datasets = [ 'rogers-1', 'rogers-2' ];
    const percentile = [0, 0.25, 0.5, 0.75, 1];
    const features = [ 'overlap', 'slope', 'orthogonality', 'crossings', 'relCrossingAngle', 'relNumRibbons', 'widthSd', 'mutualInfo' ];
    
    for (const d of datasets) {
        resetHistories();
        dataset = retrieveDatasetData(d);
        drawParallelSets();
        await new Promise(r => setTimeout(r, 1000));
        for (const f of features) {
            for (const p of percentile) {
                const promise = loadConfigForParallelSets(d, f, 'percentile/' + p);
                console.log('Waiting.');
                await promise;
                console.log('Resolved.');
                await new Promise(r => setTimeout(r, 1000));
                vis.transition().duration(500).call(chart.tension(0.5));
                await new Promise(r => setTimeout(r, 1000));
                downloadPng(document.querySelector('svg'), d + '-' + f + '-' + p + '-' + roundThreeDecimals(metrics()[f]), { css: 'inline' });
                await new Promise(r => setTimeout(r, 1000));
            }
        }
    }

}

// --- Constraints of the analyst (constraint-state.js), set in context menus on the plot (constraint-menu.js) -------

let constraintState = createState();
let constraintDataset = null;
let constraintMenu = null;

const shownConfig = () => normalizeConfig(window.psOrdering);

function element(tag, properties, children = []) {
    const node = Object.assign(document.createElement(tag), properties);
    children.forEach(child => node.append(child));
    return node;
}

// Before every draw: another dataset starts without constraints (todo/11 Decision 6); otherwise the constraints are
// reconciled with the configuration to show (e.g., after a lookup, "Optimize", or Undo).
function syncConstraints() {
    if (!constraintMenu) return;      // before initConstraints
    constraintMenu.close();
    if (constraintDataset !== dataset.name) {
        constraintState = createState();
        constraintDataset = dataset.name;
        constraintMenu.clearSelection();
    }
    reconcile(constraintState, shownConfig());
    updateLookupHint();
}

// After dragging or sorting, and after a change of the constraints that keeps the configuration.
function configurationChanged() {
    if (!constraintMenu) return;
    reconcile(constraintState, shownConfig());
    constraintMenu.decorate();
    updateLookupHint();
}

function updateLookupHint() {
    const hint = document.getElementById('lookup-filter-hint');
    const filter = lookupFirstDimension(constraintState, shownConfig());
    hint.textContent = filter
        ? `Lookups (\u2193 \u2191, percentile): configurations with ${filter.name} first in its fixed order; other constraints do not filter the lookups.`
        : 'Lookups (\u2193 \u2191, percentile): all configurations; only a first axis with fixed position and fixed order filters them.';
}

// Hooks of lib/d3.parsets.js: dragging and sorting keep the constraints; menus, selection, and marks on the plot.
function initConstraints() {
    // The request of an optimization holds the constraints and the configuration at its start: no change from there
    // to its result, also while the warning of the size guard waits for "Start anyway".
    window.psAllowCategories = (name, proposed) => {
        if (optimizeWorker) return false;
        const attribute = shownConfig().find(a => a.name === name);
        return !attribute || allowsCategories(constraintState, attribute, proposed);
    };
    window.psAllowAttributes = proposed => !optimizeWorker && allowsAttributes(constraintState, shownConfig(), proposed);
    window.psConstraints = () => toRequest(constraintState, shownConfig());
    constraintMenu = initConstraintMenu({
        state: () => constraintState,
        config: shownConfig,
        dataset: () => window.psDataset,
        locked: () => !!optimizeWorker,
        show: config => showConfiguration(dataset.name, config),
        changed: configurationChanged,
    });
}

// --- Optimize: one action per category ordering-dependent metric, in a Web Worker (optimizers/worker.js) ---------

let optimizeWorker = null;
let optimizeRun = null;         // { datasetName, metric, previous, best: last progress message }

function optimizeStatus(text) {
    document.getElementById('optimize-status').textContent = text;
}

function optimizeLine(r, state) {
    const count = (r.nodes > 0 ? r.nodes.toLocaleString('en-US') + ' nodes, ' : '')
        + (r.evaluations > 0 ? r.evaluations.toLocaleString('en-US') + ' evaluations, ' : '');
    return `${METRICS[r.metric].label} ${roundThreeDecimals(r.value)} (${state}; ${count}`
        + `${(r.time / 1000).toFixed(2)} s; configuration ${r.id})`;
}

function optimizeFinish(r, state) {
    const run = optimizeRun;
    if (optimizeWorker) optimizeWorker.terminate();
    optimizeWorker = null;
    optimizeRun = null;
    optimizeWarning(null);
    document.getElementById('optimize-cancel').style.display = 'none';
    if (!r) {
        optimizeStatus(state);
        window.optimizeResult = { error: state };
        return;
    }
    optimizeStatus(optimizeLine(r, state));
    const undo = document.getElementById('optimize-undo');
    undo.style.display = 'inline-block';
    undo.onclick = () => {
        undo.style.display = 'none';
        optimizeStatus('');
        showConfiguration(run.datasetName, run.previous);
    };
    showConfiguration(run.datasetName, r.config).then(() => { window.optimizeResult = { ...r, state }; });
}

// Warning of the size guard (size-guard.js): reasons and "Start anyway"; hidden if reasons is null.
function optimizeWarning(reasons, start = null) {
    const box = document.getElementById('optimize-warning');
    box.replaceChildren();
    box.style.display = reasons ? 'block' : 'none';
    if (!reasons) return;
    const list = element('ul', {}, reasons.map(reason => element('li', { textContent: reason })));
    const go = element('button', { type: 'button', id: 'optimize-start-anyway', className: 'btn btn-outline-dark btn-sm', textContent: 'Start anyway' });
    go.onclick = start;
    box.append('This run may take long:', list, go, ' The run can be cancelled; the best layout found so far is kept.');
}

// optimizer: null = the method of the metric; 'barycenter' = the fast option (button "Fast", todo/19).
function optimizeMetric(metric, optimizer = null) {
    if (optimizeWorker || !window.psDataset) return;
    const svg = document.getElementById('vis-svg');
    const previous = normalizeConfig(window.psOrdering);
    // The first-attribute modes are states of the constraints (todo/11): user-fixed and automatic = a fixed position.
    const request = {
        dataset: window.psDataset, metric, mode: 'unconstrained', config: previous, optimizer,
        constraints: toRequest(constraintState, previous),
        layoutOptions: { width: +svg.getAttribute('width'), height: +svg.getAttribute('height') },
    };
    const size = runSize(window.psDataset, previous, request.constraints, metric, request.mode, optimizer);
    optimizeRun = { datasetName: dataset.name, metric, previous, best: null };
    window.optimizeResult = null;
    window.optimizeWarning = null;
    optimizeWarning(null);
    document.getElementById('optimize-undo').style.display = 'none';
    document.getElementById('optimize-cancel').style.display = 'inline-block';
    optimizeWorker = new Worker(new URL('./optimizers/worker.js', import.meta.url));
    const worker = optimizeWorker;
    const start = () => {
        optimizeWarning(null);
        optimizeStatus(`${METRICS[metric].label}: optimizing ...`);
        worker.postMessage(request);
    };
    // Size guard: a run that is expected to take long starts only after the analyst confirmed it.
    const decide = estimate => {
        const reasons = runWarnings(size, estimate);
        if (reasons.length === 0) { start(); return; }
        optimizeStatus(`${METRICS[metric].label}: waiting for confirmation`);
        optimizeWarning(reasons, start);
        window.optimizeWarning = { metric, reasons, estimate, size: { ...size, dataset: undefined } };
    };
    optimizeWorker.onmessage = event => {
        const message = event.data;
        if (message.type === 'estimate') {
            decide(message);
        } else if (message.type === 'progress') {
            optimizeRun.best = message;
            const left = remainingSeconds(message.order, message.orders, message.time);
            optimizeStatus(`${METRICS[metric].label}: attribute order ${message.order} of ${message.orders}, best ${roundThreeDecimals(message.value)}, `
                + `${(message.nodes > 0 ? message.nodes : message.evaluations).toLocaleString('en-US')} ${message.nodes > 0 ? 'nodes' : 'evaluations'}`
                + (left > 5 ? `, about ${formatDuration(left)} left` : ''));
        } else if (message.type === 'result') {
            optimizeFinish(message, message.status);
        } else {
            optimizeFinish(null, 'Optimization failed: ' + message.message);
        }
    };
    optimizeWorker.onerror = event => optimizeFinish(null, 'Optimization failed: ' + event.message);
    if (canEstimate(size)) {
        optimizeStatus(`${METRICS[metric].label}: estimating the run time ...`);
        worker.postMessage({ ...request, estimate: true, attributeOrders: size.attributeOrders });
    } else {
        decide(null);
    }
}

// A new dataset ends a running optimization without applying a result; the undo of the old dataset is dropped.
function optimizeAbort() {
    if (optimizeWorker) optimizeWorker.terminate();
    optimizeWorker = null;
    optimizeRun = null;
    document.getElementById('optimize-cancel').style.display = 'none';
    document.getElementById('optimize-undo').style.display = 'none';
    optimizeWarning(null);
    optimizeStatus('');
}

// A cancelled run applies the best configuration found so far.
function optimizeCancel() {
    if (!optimizeRun) return;
    if (optimizeRun.best) optimizeFinish(optimizeRun.best, 'cancelled, best found');
    else optimizeFinish(null, 'Optimization cancelled.');
}

window.onload = () => {
    initConstraints();
    initUpload();
    window.optimizeMetric = optimizeMetric;
    window.optimizeCancel = optimizeCancel;
    window.showConfiguration = showConfiguration;
    window.curves = curves;

    window.selectDataset = selectDataset;
    window.loadUserCsv = loadUserCsv;
    window.loadUserFile = loadUserFile;
    window.isUserDataset = isUserDataset;

    window.overlapMin = overlapMin;
    window.overlapMax = overlapMax;
    window.slopeMin = slopeMin;
    window.slopeMax = slopeMax;
    window.orthogonalityMin = orthogonalityMin;
    window.orthogonalityMax = orthogonalityMax;
    window.numCrossingsMin = numCrossingsMin;
    window.numCrossingsMax = numCrossingsMax;
    window.crossingAngleMin = crossingAngleMin;
    window.crossingAngleMax = crossingAngleMax;
    window.numRibbonsMin = numRibbonsMin;
    window.numRibbonsMax = numRibbonsMax;
    window.widthVarianceMin = widthVarianceMin;
    window.widthVarianceMax = widthVarianceMax;
    window.mutualInfoMin = mutualInfoMin;
    window.mutualInfoMax = mutualInfoMax;

    window.overlapPercentile =  overlapPercentile;
    window.slopePercentile =  slopePercentile;
    window.orthogonalityPercentile = orthogonalityPercentile;
    window.numCrossingsPercentile = numCrossingsPercentile;
    window.crossingAnglePercentile = crossingAnglePercentile;
    window.numRibbonsPercentile = numRibbonsPercentile;
    window.widthVariancePercentile = widthVariancePercentile;
    window.mutualInfoPercentile = mutualInfoPercentile;

    window.percentileValueChange = percentileValueChange;

    window.featurePercentile = featurePercentile;

    const exportName = () => dataset.name.replace(USER_PREFIX, '') + '-ps';
    // The export leaves out the marks of the constraints and the selection (todo/11 Decision 8).
    const exportPlot = download => () => {
        const run = () => download(document.querySelector('svg'), exportName(), { css: 'inline' });
        return constraintMenu ? constraintMenu.withoutMarks(run) : run();
    };
    window.downloadSvg = exportPlot(downloadSvg);
    window.downloadPng = exportPlot(downloadPng);

    selectDataset('titanic');

    //crawlPngImages();
}

